import React, { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import { ThemeProviderV2, IconButton } from '@blueprint/ui';
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import usersApi from "../../api/users";
import { NoRecordCard } from "../../common/components";
import { Loader, Table } from "../../common/components/core";
import { User } from "../../types/users";
import { PageTitle } from "../../types/pageTitle";
import {
    parseRoleToText,
} from "../../utils/role";

import UserStatusBox from "./components/UserStatusBox";
import useFilterHandler, {
    FilterUserManagementListFormValues,
} from "./hooks/useFilterHandler";
import { generateTypeDisplayName } from "../EntityManagement/ProfileManageMentView";
import { ResponseWithPagination } from "../../types/common";

const UserManagementView = (): JSX.Element => {
    document.title = PageTitle.UserManagementView;
    return <PureUserManagementView />;
};

export const PureUserManagementView = (): JSX.Element => {
    // const [pageNo, setPageNo] = useState<number>(0);
    const pageSize = 10;
    const history = useHistory();
    const [filter, setFilter] = useState<FilterUserManagementListFormValues>({
        emails: [],
        profileType: "",
        roleType: "",
        profileName: "",
        pageNo: 0,
    });
    // const [userList, setUserList] = useState<User[]>([]);
    const [userListPaging, setUserListPaging] = useState<ResponseWithPagination<User[]>>();

    const [isUserListLoading, setIsUserListLoading] = useState(false);


    const nextPage = () => {
        if (userListPaging && !userListPaging.last) {
            // setPageNo(pageNo + 1);
            setFilter((olddata)=>{
                return {
                    ...olddata,
                    pageNo: olddata.pageNo + 1
                }
            })
        }
    };
    const prevPage = () => {
        if (filter.pageNo > 0) {
            // setPageNo(pageNo - 1);
            setFilter((olddata)=>{
                return {
                    ...olddata,
                    pageNo: olddata.pageNo - 1
                }
            })
        }
    };
    const toPage = (page: number) => {
        if (userListPaging && page !== userListPaging.number) {
            if (page >= 0 && page < userListPaging.totalPages) {
                // setPageNo(page);
                setFilter((oldData) => {
                    return {
                        ...oldData,
                        pageNo: page
                    }
                })
            }
        }
    };

    const { show } = useFilterHandler(
        filter,
        (values: FilterUserManagementListFormValues) => { setFilter({...values, pageNo: 0}); },
    );

    useEffect(() => {
        const getUserList = async () => {
            try {
                setIsUserListLoading(true);
                const userListRes = await usersApi.getUsersPaging(
                    filter.emails,
                    filter.profileType,
                    filter.roleType,
                    filter.profileName,
                    filter.pageNo,
                    pageSize
                );
                setUserListPaging(userListRes);
                setIsUserListLoading(false);
            } catch (e) {
                console.error(e);
            }
        };

        getUserList();
    }, [filter]);


    return (
        <>
            <ThemeProviderV2>
            <div className="d-flex align-items-center mb-4">
                <h1 className="flex-grow-1 mb-0">User Management</h1>

                <div>
                    <IconButton
                        icon="Filter"
                        variant="light.tertiary"
                        onClick={show}
                        style={{marginRight: '20px'}}
                    >
                        Filter
                    </IconButton>
                    <IconButton
                        icon="Add"
                        variant="light.primary"
                        onClick={() =>
                            history.push("/user-management/create-new-user")
                        }
                    >
                        Create New User
                    </IconButton>
                </div>
            </div>
            <Loader isLoading={isUserListLoading}>
                {userListPaging && userListPaging.content.length ? (
                    <Card className="mt-4 border-0">
                        <Card.Body className="px-0 py-0">
                            <table className="custom-table">
                                <thead>
                                    <tr>
                                        <Table.HeaderCell width={120}>
                                            Name
                                        </Table.HeaderCell>
                                        <Table.HeaderCell width={200}>
                                            Email Address
                                        </Table.HeaderCell>
                                        <Table.HeaderCell width={120}>
                                            Profile Type
                                        </Table.HeaderCell>
                                        <Table.HeaderCell width={180}>
                                            Profile Name
                                        </Table.HeaderCell>
                                        <Table.HeaderCell width={160}>
                                            Role
                                        </Table.HeaderCell>
                                        <Table.HeaderCell width={100}>
                                            Status
                                        </Table.HeaderCell>
                                    </tr>
                                </thead>
                                <tbody>
                                    {userListPaging.content
                                        .map((data, index) => {
                                            return (
                                                <Table.BodyRow
                                                    key={index}
                                                    onClick={() =>
                                                        history.push(
                                                            `/user-management/user-information/${data.id}`,
                                                        )
                                                    }
                                                    onKeyUp={(event:KeyboardEvent) =>
                                                        {(event.keyCode === 13 || event.keyCode === 32) &&
                                                            history.push(
                                                                `/user-management/user-information/${data.id}`,
                                                            )
                                                        }
                                                    }
                                                >
                                                    <StyledTd className="cta-cell">
                                                        {data.firstName}{" "}
                                                        {data.lastName}
                                                    </StyledTd>
                                                    <StyledTd>
                                                        {data.email}
                                                    </StyledTd>
                                                    <StyledTd>
                                                        {generateTypeDisplayName(
                                                            data.profileType,
                                                        )}
                                                    </StyledTd>
                                                    <StyledTd>
                                                        {data.profileName}
                                                    </StyledTd>
                                                    <StyledTd>
                                                        {parseRoleToText(
                                                            data.role,
                                                        )}
                                                    </StyledTd>
                                                    <StyledTd>
                                                        <UserStatusBox
                                                            status={data.status}
                                                        />
                                                    </StyledTd>
                                                </Table.BodyRow>
                                            );
                                        })}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan={6}>
                                            <Table.Pagination
                                                backArrowOnClick={prevPage}
                                                nextArrowOnClick={nextPage}
                                                backArrowDisabled={userListPaging.first}
                                                nextArrowDisabled={userListPaging.last}
                                                totalPage={userListPaging.totalPages}
                                                currentPage={userListPaging.number + 1}
                                                onPageNumberChange={(page) => toPage(page - 1)}
                                            />
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </Card.Body>
                    </Card>
                ) : (
                    <NoRecordCard />
                )}
            </Loader>
        </ThemeProviderV2>
      </>
    );
};


const StyledTd = styled.td`
    vertical-align: top;
`;

export default UserManagementView;
